import { getCmsContentId } from 'src/helpers/utils';
import { EnumCmsPageTypeEnum, EnumCmsWidgetTypes } from 'src/types/cms/cmsEnums';
import {
  CmsCategoryPageResponse,
  CmsHomePagesResponse,
  CmsInfoPagesResponse,
  CmsPromotionPagesResponse,
  CmsRafflePageResponse,
} from 'src/types/cms/cmsPageTypes';
import {
  CmsMenuWidget,
  CmsNewsletter,
  CmsRafTerms,
  CmsSocialLink,
  CmsWebsiteFooter,
  CmsWidget,
} from 'src/types/cms/cmsWidgetTypes';
import { EnumLangCodes } from 'src/utils/constants';
import HttpClient from './http';

export const getPromotions = (locale: string): Promise<CmsPromotionPagesResponse> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsPageTypeEnum.PROMO_POST);
  search.append('status', 'active');
  search.append('skinId', cmsContentId);
  return HttpClient.get(
    {
      path: `pages/getPagesPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getHomePage = (locale: string): Promise<CmsHomePagesResponse> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsPageTypeEnum.HOME_PAGE);
  search.append('skinId', cmsContentId);
  search.append('slug', 'home');

  return HttpClient.get(
    {
      path: `pages/getPagePublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getLoginPage = (locale: string): Promise<CmsHomePagesResponse> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsPageTypeEnum.LOGIN_PAGE);
  search.append('skinId', cmsContentId);
  search.append('slug', 'login');

  return HttpClient.get(
    {
      path: `pages/getPagePublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getSignupPage = (locale: string): Promise<CmsHomePagesResponse> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsPageTypeEnum.SIGNUP_PAGE);
  search.append('skinId', cmsContentId);
  search.append('slug', 'signup');

  return HttpClient.get(
    {
      path: `pages/getPagePublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getCategoryPage = (locale: string, slug: string): Promise<CmsCategoryPageResponse> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('skinId', cmsContentId);
  search.append('type', EnumCmsPageTypeEnum.CATEGORY_PAGE);
  search.append('slug', slug);

  return HttpClient.get(
    {
      path: `pages/getPagePublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getBlogs = (locale: string): Promise<CmsPromotionPagesResponse> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsPageTypeEnum.BLOG_ARTICLE);
  search.append('status', 'active');
  search.append('skinId', cmsContentId);
  return HttpClient.get(
    {
      path: `pages/getPagesPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getInfoPages = (locale: string): Promise<CmsInfoPagesResponse> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsPageTypeEnum.INFO_PAGE);
  search.append('status', 'active');
  search.append('skinId', cmsContentId);
  return HttpClient.get(
    {
      path: `pages/getPagesPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getNewsletter = (locale: string): Promise<{ result: CmsWidget<CmsNewsletter>[]; success: boolean }> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsWidgetTypes.NEWSLETTER);
  search.append('skinId', cmsContentId);

  return HttpClient.get(
    {
      path: `widgets/getWidgetsPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getRafTerms = async (locale: string): Promise<{ result: CmsWidget<CmsRafTerms>[]; success: boolean }> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsWidgetTypes.RAF_TERMS_AND_CONDITIONS);
  search.append('skinId', cmsContentId);

  return await HttpClient.get(
    {
      path: `widgets/getWidgetsPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getFooterConfigs = (
  locale: string
): Promise<{ result: CmsWidget<CmsWebsiteFooter>[]; success: boolean }> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsWidgetTypes.WEBSITE_FOOTER);
  search.append('skinId', cmsContentId);

  return HttpClient.get(
    {
      path: `widgets/getWidgetsPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getSocialLinks = (locale: string): Promise<{ result: CmsWidget<CmsSocialLink>[]; success: boolean }> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsWidgetTypes.SOCIALS_LINK);
  search.append('skinId', cmsContentId);

  return HttpClient.get(
    {
      path: `widgets/getWidgetsPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getMenuWidget = (locale: string): Promise<{ result: CmsWidget<CmsMenuWidget>[]; success: boolean }> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsWidgetTypes.MENU_WIDGET);
  search.append('skinId', cmsContentId);

  return HttpClient.get(
    {
      path: `widgets/getWidgetsPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getRafflesBanners = (locale: string): Promise<CmsRafflePageResponse> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsPageTypeEnum.RAFFLE_PAGE);
  search.append('status', 'active');
  search.append('skinId', cmsContentId);

  return HttpClient.get(
    {
      path: `pages/getPagesPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};

export const getPokerBanners = (locale: string): Promise<CmsRafflePageResponse> => {
  const cmsContentId = getCmsContentId();
  const search = new URLSearchParams();
  search.append('lang', EnumLangCodes[locale]);
  search.append('type', EnumCmsPageTypeEnum.POKER_PAGE);
  search.append('status', 'active');
  search.append('skinId', cmsContentId);

  return HttpClient.get(
    {
      path: `pages/getPagesPublic?${search.toString()}`,
      baseURL: process.env.REACT_APP_CMS_ADDRESS as string,
    },
    false
  );
};
